const UNIT_DEFINITIONS = {
  kg: { family: 'mass', factor: 1000, label: 'kg', baseUnit: 'g' },
  g: { family: 'mass', factor: 1, label: 'g', baseUnit: 'g' },
  gram: { family: 'mass', factor: 1, label: 'g', baseUnit: 'g' },
  grams: { family: 'mass', factor: 1, label: 'g', baseUnit: 'g' },
  gm: { family: 'mass', factor: 1, label: 'g', baseUnit: 'g' },
  liter: { family: 'volume', factor: 1000, label: 'liter', baseUnit: 'ml' },
  liters: { family: 'volume', factor: 1000, label: 'liter', baseUnit: 'ml' },
  litre: { family: 'volume', factor: 1000, label: 'liter', baseUnit: 'ml' },
  litres: { family: 'volume', factor: 1000, label: 'liter', baseUnit: 'ml' },
  l: { family: 'volume', factor: 1000, label: 'liter', baseUnit: 'ml' },
  ml: { family: 'volume', factor: 1, label: 'ml', baseUnit: 'ml' },
  pcs: { family: 'count', factor: 1, label: 'pcs', baseUnit: 'pcs' },
  pc: { family: 'count', factor: 1, label: 'pcs', baseUnit: 'pcs' },
  piece: { family: 'count', factor: 1, label: 'pcs', baseUnit: 'pcs' },
  pieces: { family: 'count', factor: 1, label: 'pcs', baseUnit: 'pcs' },
};

export const normalizeRecipeUnit = (unit = '') => {
  const normalized = String(unit || '').trim().toLowerCase();
  return UNIT_DEFINITIONS[normalized]?.label || normalized || 'pcs';
};

export const getRecipeUnitDefinition = (unit = '') => (
  UNIT_DEFINITIONS[String(unit || '').trim().toLowerCase()] || {
    family: 'custom',
    factor: 1,
    label: normalizeRecipeUnit(unit),
    baseUnit: normalizeRecipeUnit(unit),
  }
);

export const areRecipeUnitsCompatible = (fromUnit, toUnit) => {
  const from = getRecipeUnitDefinition(fromUnit);
  const to = getRecipeUnitDefinition(toUnit);
  return from.family !== 'custom' && from.family === to.family;
};

export const convertRecipeQuantity = (quantity, fromUnit, toUnit) => {
  const numeric = Number(quantity);
  if (!Number.isFinite(numeric)) return 0;

  const from = getRecipeUnitDefinition(fromUnit);
  const to = getRecipeUnitDefinition(toUnit);
  if (from.label === to.label) return numeric;
  if (!areRecipeUnitsCompatible(fromUnit, toUnit)) {
    throw new Error(`Cannot convert ${from.label} to ${to.label}.`);
  }

  return (numeric * from.factor) / to.factor;
};

export const parseRecipeQuantity = (value, { allowZero = false } = {}) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return { ok: false, value: 0, message: 'Quantity must be a valid number.' };
  }
  if (numeric < 0 || (!allowZero && numeric === 0)) {
    return {
      ok: false,
      value: numeric,
      message: allowZero ? 'Quantity cannot be negative.' : 'Quantity must be greater than zero.',
    };
  }
  return { ok: true, value: Number(numeric.toFixed(3)), message: '' };
};

export const formatRecipeQuantity = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric.toFixed(3) : '0.000';
};

export const getRecipeInputStep = () => '0.001';

export const getRecipeQuantityHint = (unit = '') => {
  const normalized = normalizeRecipeUnit(unit);
  if (normalized === 'kg') return 'Use kg. Example: 150g = 0.150 kg';
  if (normalized === 'liter') return 'Use liters. Example: 150ml = 0.150 liter';
  if (normalized === 'g') return 'Use grams. Example: 150g = 150.000 g';
  if (normalized === 'ml') return 'Use ml. Example: 150ml = 150.000 ml';
  return `Use ${normalized}. Values support 3 decimals.`;
};
